import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { t } from '@aquilixshop/i18n';
import { useProductState } from '../_shared/productContext.js';
import { OfferTiles } from '../_shared/OfferTiles.js';
import { OFFER_TILE_VARS } from '../_shared/offerTileVars.js';
import { marginFields, visibilityField } from '../_shared/blockFields.js';
import { marginStyle, visibilityCss } from '../_shared/blockRender.js';
/**
 * volume-tiers — the Releasit-style bundle / volume selector as a STANDALONE, droppable buy-box block.
 *
 * The selector engine already exists ({@link OfferTiles}, feature 0026-07) — but until now it rendered
 * ONLY inside {@link cod-form}, so a buy-box built with plain `buy-buttons` (no COD form) could not show
 * the bundle tiers. This block exposes the SAME component anywhere in `main-product > details`: it reads
 * the product's `context.activeOffer` (configured via the quantity-offers feature) and drives the shared
 * cart `quantity`, so `price`, `buy-buttons`, `cod-form` and checkout all track the selected tier.
 *
 * FAIL-OPEN on the live storefront (no enabled offer for this product → renders nothing). In the BUILDER
 * it shows a hint so the empty block is not a mystery.
 *
 * The tiles read cod-form's `--cf-*` design tokens; standalone we map them onto the section's
 * `--aqx-scheme-*` colour-scheme vars so the selector inherits the buy-box theme (accent / border / …).
 */
// The `--cf-*` mapping lives in `_shared/offerTileVars` since 0069 lot B: the catalog-mode buy-box needs
// the very same one, and two copies of a colour map drift.
const CF_VARS = OFFER_TILE_VARS;
export const volumeTiersBlockSchema = {
    type: 'volume-tiers',
    name: 'Bundle tiers',
    max: 1,
    fields: [
        // The one design choice this block exposes (founder, 24/09: « l'image doit être configurable, on doit
        // pouvoir choisir de la mettre ou non »). Shares its label key with cod-form's field of the same id.
        {
            id: 'offerShowImage',
            type: 'checkbox',
            label: 'Show product image',
            default: true,
            info: 'Untick for tiles without the product thumbnail.',
        },
        ...marginFields(18, 18),
        visibilityField,
    ],
};
const VolumeTiersBlock = ({ block, context }) => {
    const ps = useProductState();
    const bs = block.settings;
    const cls = `aqx-vt-${block.id}`;
    const style = {
        ...CF_VARS,
        ...(bs.offerShowImage === false ? { '--qo-thumb-display': 'none' } : {}),
        ...marginStyle(bs, 18, 18),
    };
    const offer = context.activeOffer;
    const hasTiers = !!ps && !!offer && offer.productId === ps.product.id && offer.tiers.length > 0;
    let inner = null;
    if (hasTiers) {
        // Standalone = its own CARD, closed by a «Total» line (28/09/2026): here the tiles are not inside
        // an order form, so nothing else frames them and nothing else says what the pack costs.
        inner = _jsx(OfferTiles, { context: context, className: `${cls}-tiles`, framed: true });
    }
    else if (context.renderMode === 'edit') {
        // Builder-only hint: the selector needs a quantity offer configured on this product.
        inner = (_jsxs("div", { style: {
                border: '1px dashed var(--aqx-scheme-border, #d3d3db)',
                borderRadius: 12,
                padding: '18px 16px',
                font: '13px/1.5 system-ui, sans-serif',
                color: 'color-mix(in srgb, var(--aqx-scheme-text, #1b1b1e) 60%, transparent)',
                textAlign: 'center',
            }, children: [_jsx("strong", { children: t(context, 'builder.volume_tiers_empty_title') }), ' — ', t(context, 'builder.volume_tiers_empty_body')] }));
    }
    // Fail-open on the live storefront: nothing to show → render nothing (no empty wrapper).
    if (inner === null)
        return null;
    return (_jsxs("div", { className: cls, style: style, children: [_jsx("style", { dangerouslySetInnerHTML: { __html: visibilityCss(cls, bs.visibility) } }), inner] }));
};
export const volumeTiersBlock = {
    type: 'volume-tiers',
    schema: volumeTiersBlockSchema,
    Component: VolumeTiersBlock,
    meta: { label: 'Bundle tiers' },
};
//# sourceMappingURL=index.js.map